'use client';

import React, { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo, useId } from 'react';
import { PhotoIcon, XMarkIcon, CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import type { WebRegImportReceipt } from '../scheduling/useWebRegScheduleImport';
import { RutgersCourse } from '@/app/types/interfaces';
import { webregOcrApi } from '@/app/services/api';
import { loadCatalogCourses as getParsedCourses } from '@/app/services/courseCatalogClient';

interface ExtractedCourse {
    baseId: string;        // e.g., "01198323"
    courseCode: string;    // e.g., "01:198:323"
    sectionNumber: string; // e.g., "03"
    indexNumber: string;   // e.g., "11665"
    matchedCourse?: RutgersCourse;
    matchedSection?: RutgersCourse;
}

interface WebRegImportProps {
    onImport: (courses: Array<{ baseId: string; sectionId: string }>, scheduleName: string, review: WebRegImportReceipt) => void | false | Promise<void | boolean>;
    onClose: () => void;
    semester?: string;
    existingScheduleCount?: number;
}

export default function WebRegImport({
    onImport,
    onClose,
    semester = 'fall-2026',
    existingScheduleCount = 0
}: WebRegImportProps) {
    const imageContext = useMemo(() => ({ semester }), [semester]);
    const [isImporting, setIsImporting] = useState(false);
    const importPendingRef = useRef<symbol | null>(null);
    const draftRef = useRef<object>({});
    const reviewNameRef = useRef<{ draft: object; name: string } | null>(null);
    const mountedRef = useRef<symbol | null>(null);
    useLayoutEffect(() => {
        const epoch = Symbol('webreg-review'); mountedRef.current = epoch; draftRef.current = {};
        reviewNameRef.current = null; setExtractedCourses([]); setError(null); setIsProcessing(false); setProgress(0); setImagePreview(null); setRawText('');
        return () => { if (mountedRef.current === epoch) { mountedRef.current = null; draftRef.current = {}; } };
    }, [imageContext]);
    const retireDraft = () => { draftRef.current = {}; reviewNameRef.current = null; };
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [extractedCourses, setExtractedCourses] = useState<ExtractedCourse[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [rawText, setRawText] = useState<string>('');
    const committedReviewRef = useRef<{ courses: ExtractedCourse[]; semester: string; imageContext: object } | null>(null);
    useLayoutEffect(() => { committedReviewRef.current = { courses: extractedCourses, semester, imageContext }; });
    const fileInputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const imageInputId = useId();
    const uploadHelpId = useId();
    const resultsHeadingRef = useRef<HTMLHeadingElement>(null);
    const processingRef = useRef<HTMLDivElement>(null);
    const errorRef = useRef<HTMLDivElement>(null);
    const resetButtonRef = useRef<HTMLButtonElement>(null);
    const previouslyHadPreview = useRef(false);

    useEffect(() => {
        if (isProcessing) processingRef.current?.focus();
        else if (error) errorRef.current?.focus();
        else if (extractedCourses.length) resultsHeadingRef.current?.focus();
        else if (imagePreview) resetButtonRef.current?.focus();
        else if (!imagePreview && previouslyHadPreview.current) fileInputRef.current?.focus();
        previouslyHadPreview.current = Boolean(imagePreview);
    }, [error, extractedCourses.length, imagePreview, isProcessing]);

    // Process image using Groq Vision API via Supabase edge function
    const processImage = useCallback(async (file: File | Blob) => {
        if (importPendingRef.current) return;
        const epoch = mountedRef.current;
        if (!epoch || committedReviewRef.current?.imageContext !== imageContext) return;
        const draft = {}; draftRef.current = draft; reviewNameRef.current = null;
        const isCurrent = () => mountedRef.current === epoch && draftRef.current === draft;
        setIsProcessing(true);
        setError(null);
        setProgress(0);
        setExtractedCourses([]); setImagePreview(null); setRawText('');

        try {
            console.log('[WebRegImport] Starting Groq Vision OCR...');
            setProgress(10);

            // Convert file to base64
            const base64 = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result as string);
                reader.onerror = () => reject(new Error('The screenshot could not be read.'));
                reader.readAsDataURL(file);
            });

            if (!isCurrent()) return;
            setImagePreview(base64);
            console.log('[WebRegImport] Image converted to base64, calling Groq API...');
            setProgress(30);

            // Call Groq Vision API via Supabase edge function
            const result = await webregOcrApi.extractCourses(base64);

            if (!isCurrent()) return;
            console.log('[WebRegImport] Groq API response:', result);
            setProgress(70);

            if (!result.success || !result.courses || result.courses.length === 0) {
                setError('No course codes found in the image. Make sure the image shows WebReg course codes like "01:198:323:03:11665".');
                if (result.rawResponse) {
                    setRawText(result.rawResponse);
                }
                return;
            }

            setRawText(result.rawResponse || JSON.stringify(result.courses, null, 2));

            // Get all courses for matching sections in database
            const allCourses = await getParsedCourses(semester);
            if (!isCurrent()) return;
            console.log('[WebRegImport] Total courses in database:', allCourses.length);

            // Match extracted courses with database
            const seenIndexes = new Set<string>();
            const matchedCourses: ExtractedCourse[] = [];

            for (const course of result.courses) {
                // Skip duplicates
                if (seenIndexes.has(course.index)) continue;
                seenIndexes.add(course.index);

                // Parse course code to get baseId
                const parts = course.courseCode.split(':');
                if (parts.length !== 3) {
                    console.log('[WebRegImport] Invalid course code format:', course.courseCode);
                    continue;
                }
                const baseId = parts.join('').replace(/\D/g, '');

                // Find matching course in database
                const courseSections = allCourses.filter(c => c.baseId === baseId);
                const matchedCourse = courseSections[0];

                // Find the specific section by index number (most reliable)
                let matchedSection = courseSections.find(c => c.id === course.index);

                // Fallback: match by section number
                if (!matchedSection && course.section) {
                    matchedSection = courseSections.find(c => {
                        const dbSection = c.section?.toString().padStart(2, '0');
                        const inputSection = course.section.padStart(2, '0');
                        return dbSection === inputSection || c.section === course.section;
                    });
                }

                console.log('[WebRegImport] Course:', course.courseCode,
                    'matched:', matchedCourse?.courseName,
                    'section:', matchedSection?.section);

                matchedCourses.push({
                    baseId,
                    courseCode: course.courseCode,
                    sectionNumber: course.section,
                    indexNumber: course.index,
                    matchedCourse,
                    matchedSection
                });
            }

            setProgress(100);

            if (matchedCourses.length === 0) {
                setError('No matching courses found in the database for the current semester.');
            } else {
                setExtractedCourses(matchedCourses);
            }

        } catch (err) {
            if (!isCurrent()) return;
            console.error('[WebRegImport] OCR error:', err);
            setError('Failed to process image. Please try again.');
        } finally {
            if (isCurrent()) { setIsProcessing(false); setProgress(0); }
        }
    }, [semester, imageContext]);

    // Handle paste event for clipboard images
    const handlePaste = useCallback(async (e: ClipboardEvent) => {
        if (importPendingRef.current || !mountedRef.current || committedReviewRef.current?.imageContext !== imageContext) return;
        const items = e.clipboardData?.items;
        if (!items) return;

        for (const item of items) {
            if (item.type.startsWith('image/')) {
                e.preventDefault();
                const file = item.getAsFile();
                if (!file) continue;

                console.log('[WebRegImport] Image pasted from clipboard');

                // Process with OCR
                await processImage(file);
                break;
            }
        }
    }, [processImage, imageContext]);

    // Listen for paste events
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        // Listen on document for paste events when this component is mounted
        document.addEventListener('paste', handlePaste);

        return () => {
            document.removeEventListener('paste', handlePaste);
        };
    }, [handlePaste]);

    // Handle file selection
    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (importPendingRef.current || !mountedRef.current || committedReviewRef.current?.imageContext !== imageContext) return;
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please upload an image file (PNG, JPG, or WEBP)');
            return;
        }

        // Process with OCR
        await processImage(file);
    };

    // Handle drag and drop
    const handleDrop = useCallback(async (e: React.DragEvent) => {
        e.preventDefault();
        if (importPendingRef.current || !mountedRef.current || committedReviewRef.current?.imageContext !== imageContext) return;
        const file = e.dataTransfer.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setError('Please upload an image file (PNG, JPG, or WEBP)');
            return;
        }

        await processImage(file);
    }, [processImage, imageContext]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
    }, []);

    // Handle import
    const handleImport = async () => {
        if (importPendingRef.current || isProcessing || committedReviewRef.current?.courses !== extractedCourses
            || committedReviewRef.current.imageContext !== imageContext) return;
        const coursesToImport = extractedCourses.filter(c => c.matchedSection)
            .map(c => ({ baseId: c.baseId, sectionId: c.matchedSection!.id }));
        if (coursesToImport.length === 0) {
            setError('No matching courses found in the database for the current semester.');
            return;
        }
        const epoch = mountedRef.current; const draft = draftRef.current;
        if (!epoch) return;
        if (reviewNameRef.current?.draft !== draft) reviewNameRef.current = { draft, name: `Web Reg Import ${existingScheduleCount + 1}` };
        const operation = Symbol('webreg-submit'); importPendingRef.current = operation;
        const isCurrent = () => mountedRef.current === epoch && draftRef.current === draft;
        setIsImporting(true); setError(null);
        try {
            const imported = await onImport(coursesToImport, reviewNameRef.current.name, { identity: draft, isCurrent });
            if (imported === false && isCurrent()) setError('Import could not finish. Your reviewed courses are still here. Try again when ready.');
        } catch (reason) {
            if (isCurrent()) setError(reason instanceof Error ? reason.message : 'Import could not finish. Please try again.');
        } finally {
            if (importPendingRef.current === operation) importPendingRef.current = null;
            if (mountedRef.current !== null) setIsImporting(false);
        }
    };

    // Handle removing a course from the list
    const handleRemoveCourse = (index: number) => {
        if (importPendingRef.current) return;
        retireDraft();
        setExtractedCourses(prev => prev.filter((_, i) => i !== index));
    };

    // Reset state
    const handleReset = () => {
        if (importPendingRef.current) return;
        retireDraft();
        setExtractedCourses([]);
        setError(null);
        setImagePreview(null);
        setRawText('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const matchedCount = extractedCourses.filter(c => c.matchedSection).length;
    const unmatchedCount = extractedCourses.filter(c => !c.matchedSection).length;

    return (
        <div ref={containerRef} className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                    Import from WebReg
                </h3>
                <button
                    type="button"
                    aria-label="Back to schedule selection"
                    onClick={() => { if (!importPendingRef.current) onClose(); }}
                    disabled={isImporting}
                    className="flex min-h-12 min-w-12 items-center justify-center rounded-control text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]"
                >
                    <XMarkIcon className="h-5 w-5" />
                </button>
            </div>

            <p className="text-sm text-slate-600 dark:text-slate-400">
                Upload or paste a screenshot of your WebReg schedule. We&apos;ll extract the course codes and create a new schedule.
            </p>

            {/* Upload area */}
            {!imagePreview && !isProcessing && (
                <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-control p-6 text-center"
                >
                    <PhotoIcon className="mx-auto h-12 w-12 text-slate-400" />
                    <label htmlFor={imageInputId} className="mt-2 block text-sm font-semibold text-[var(--ss-text)]">
                        Choose WebReg screenshot
                    </label>
                    <p id={uploadHelpId} className="mt-1 text-xs text-[var(--ss-text-muted)]">
                        PNG, JPG, or WEBP. You can also drop or paste an image.
                    </p>
                    <input
                        id={imageInputId}
                        aria-describedby={uploadHelpId}
                        ref={fileInputRef}
                        type="file"
                        disabled={isImporting}
                        accept="image/png,image/jpeg,image/webp"
                        onChange={handleFileSelect}
                        className="mt-3 min-h-12 w-full rounded-control border border-[var(--ss-border)] bg-[var(--ss-surface)] p-2 text-sm text-[var(--ss-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]"
                    />
                </div>
            )}

            {/* Processing indicator */}
            {isProcessing && (
                <div ref={processingRef} role="status" tabIndex={-1} className="text-center py-8">
                    <ArrowPathIcon className="mx-auto h-8 w-8 text-red-600 animate-spin" />
                    <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                        Processing image... {progress}%
                    </p>
                    <div className="mt-2 w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 max-w-xs mx-auto">
                        <div
                            className="bg-red-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>
            )}

            {/* Image preview */}
            {imagePreview && !isProcessing && (
                <div className="relative">
                    <img
                        src={imagePreview}
                        alt="WebReg schedule"
                        className="w-full rounded-control border border-slate-200 dark:border-slate-600 max-h-48 object-contain bg-slate-50 dark:bg-slate-800"
                    />
                    <button
                        type="button"
                        aria-label="Choose another screenshot"
                        ref={resetButtonRef}
                        onClick={handleReset}
                        disabled={isImporting}
                        className="absolute top-2 right-2 flex min-h-12 min-w-12 items-center justify-center bg-red-600 text-white rounded-control hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] focus-visible:ring-offset-2"
                    >
                        <XMarkIcon className="h-4 w-4" />
                    </button>
                </div>
            )}

            {/* Error message */}
            {error && (
                <div ref={errorRef} role="alert" tabIndex={-1} className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-control">
                    <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
            )}

            {/* Extracted courses */}
            {extractedCourses.length > 0 && (
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <h4 ref={resultsHeadingRef} tabIndex={-1} className="font-medium text-slate-900 dark:text-white">
                            Found {extractedCourses.length} course(s)
                        </h4>
                        {unmatchedCount > 0 && (
                            <span className="text-xs text-amber-700 dark:text-amber-300">
                                {unmatchedCount} not found in {semester === 'fall-2025' ? 'Fall 2025' : semester === 'spring-2026' ? 'Spring 2026' : 'Fall 2026'}
                            </span>
                        )}
                    </div>

                    <div className="max-h-64 overflow-y-auto space-y-2 pr-2">
                        {extractedCourses.map((course, index) => (
                            <div
                                key={`${course.baseId}-${course.sectionNumber}-${index}`}
                                className={`flex items-center justify-between p-3 rounded-control border ${course.matchedSection
                                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                    : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                                    }`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        {course.matchedSection ? (
                                            <CheckCircleIcon className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                                        ) : (
                                            <XMarkIcon className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                                        )}
                                        <span className="font-medium text-slate-900 dark:text-white">
                                            {course.courseCode}
                                        </span>
                                        <span className="text-sm text-slate-500 dark:text-slate-400">
                                            Section {course.sectionNumber}
                                        </span>
                                    </div>
                                    {course.matchedCourse && (
                                        <p className="text-sm text-slate-600 dark:text-slate-400 ml-6 truncate">
                                            {course.matchedCourse.courseName}
                                        </p>
                                    )}
                                    {!course.matchedSection && course.matchedCourse && (
                                        <p className="text-xs text-amber-700 dark:text-amber-300 ml-6">
                                            Section {course.sectionNumber} not available
                                        </p>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    aria-label={`Remove ${course.courseCode} section ${course.sectionNumber}`}
                                    onClick={() => handleRemoveCourse(index)}
                                    disabled={isImporting}
                                    className="ml-2 flex min-h-12 min-w-12 items-center justify-center rounded-control text-slate-600 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]"
                                >
                                    <XMarkIcon className="h-4 w-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-2 pt-2">
                {extractedCourses.length > 0 && (
                    <button
                        onClick={handleImport}
                        disabled={matchedCount === 0 || isImporting || isProcessing}
                        className="min-h-12 flex-1 flex items-center justify-center px-4 py-3 bg-red-600 text-white rounded-control hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] focus-visible:ring-offset-2"
                    >
                        <CheckCircleIcon className="h-5 w-5 mr-2" />
                        Import {matchedCount} Course{matchedCount !== 1 ? 's' : ''}
                    </button>
                )}
                <button
                    onClick={() => { if (!importPendingRef.current) onClose(); }}
                    disabled={isImporting}
                    className="min-h-12 px-4 py-3 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-control hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
}
